import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { getAIChat } from '../api/api';
import {
    Box, Paper, Typography, TextField, IconButton, Chip, Stack,
    CircularProgress, Avatar, Divider
} from '@mui/material';
import {
    Send as SendIcon,
    SmartToy as BotIcon,
    Person as PersonIcon,
    Lightbulb as LightbulbIcon
} from '@mui/icons-material';

// Sugestões de perguntas para o usuário
const SUGGESTED_QUESTIONS = [
    "Qual centro teve maior custo?",
    "Compare com mês anterior",
    "Há algum custo atípico?",
    "Quem recebe rateio do Global?",
    "Qual a tendência dos custos?",
    "Resuma a situação financeira"
];

const AIChatWidget = () => {
    const [messages, setMessages] = useState([
        {
            role: 'ai',
            content: 'Olá! Sou seu assistente financeiro. Analisei os dados de custos do hospital. Como posso ajudar?\n\n**Dica:** Clique nas sugestões abaixo ou digite sua pergunta.'
        }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(true);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async (messageText) => {
        const textToSend = messageText || input;
        if (!textToSend.trim()) return;

        const userMsg = { role: 'user', content: textToSend };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setLoading(true);
        setShowSuggestions(false);

        try {
            const response = await getAIChat(textToSend);
            const data = response.data;

            setMessages(prev => [...prev, { role: 'ai', content: data.response }]);
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Falha ao conectar com a IA.';
            setMessages(prev => [...prev, {
                role: 'ai',
                content: `⚠️ Erro: ${errorMessage}`
            }]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleSuggestionClick = (question) => {
        setInput(question);
        handleSend(question);
    };

    return (
        <Paper
            elevation={3}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 550,
                borderRadius: 3,
                overflow: 'hidden'
            }}
        >
            {/* Header */}
            <Box sx={{
                bgcolor: 'primary.main',
                color: 'white',
                p: 2,
                display: 'flex',
                alignItems: 'center'
            }}>
                <BotIcon sx={{ mr: 1.5 }} />
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Assistente Inteligente
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        IA Financeira
                    </Typography>
                </Box>
            </Box>

            {/* Messages Area */}
            <Box sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                bgcolor: 'grey.50',
                display: 'flex',
                flexDirection: 'column',
                gap: 2
            }}>
                {messages.map((msg, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                            gap: 1
                        }}
                    >
                        {msg.role === 'ai' && (
                            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                                <BotIcon fontSize="small" />
                            </Avatar>
                        )}
                        <Paper
                            elevation={1}
                            sx={{
                                maxWidth: '80%',
                                p: 2,
                                borderRadius: 2,
                                bgcolor: msg.role === 'user' ? 'primary.main' : 'white',
                                color: msg.role === 'user' ? 'white' : 'text.primary',
                                borderBottomRightRadius: msg.role === 'user' ? 0 : 2,
                                borderBottomLeftRadius: msg.role === 'ai' ? 0 : 2,
                                '& p': { m: 0, lineHeight: 1.6 },
                                '& ul, & ol': { pl: 2, my: 1 },
                                '& li': { mb: 0.5 },
                                '& strong': { fontWeight: 600 }
                            }}
                        >
                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </Paper>
                        {msg.role === 'user' && (
                            <Avatar sx={{ bgcolor: 'grey.400', width: 32, height: 32 }}>
                                <PersonIcon fontSize="small" />
                            </Avatar>
                        )}
                    </Box>
                ))}

                {/* Loading indicator */}
                {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                            <BotIcon fontSize="small" />
                        </Avatar>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">
                                Analisando dados...
                            </Typography>
                        </Paper>
                    </Box>
                )}

                <div ref={messagesEndRef} />
            </Box>

            {/* Suggestions */}
            {showSuggestions && !loading && (
                <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.100', borderTop: 1, borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <LightbulbIcon fontSize="small" color="warning" />
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Sugestões de perguntas:
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {SUGGESTED_QUESTIONS.map((question, idx) => (
                            <Chip
                                key={idx}
                                label={question}
                                size="small"
                                variant="outlined"
                                onClick={() => handleSuggestionClick(question)}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: 'primary.light',
                                        color: 'white',
                                        borderColor: 'primary.main'
                                    }
                                }}
                            />
                        ))}
                    </Stack>
                </Box>
            )}

            {/* Input Area */}
            <Box sx={{ p: 2, bgcolor: 'white', borderTop: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={1}>
                    <TextField
                        fullWidth
                        size="small"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Digite sua pergunta sobre os custos..."
                        disabled={loading}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3
                            }
                        }}
                    />
                    <IconButton
                        color="primary"
                        onClick={() => handleSend()}
                        disabled={loading || !input.trim()}
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'primary.dark' },
                            '&.Mui-disabled': { bgcolor: 'grey.300' }
                        }}
                    >
                        <SendIcon />
                    </IconButton>
                </Stack>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    Respostas geradas por IA. Podem conter imprecisões.
                </Typography>
            </Box>
        </Paper>
    );
};

export default AIChatWidget;
